import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { AppShell } from "@/components/app/app-shell";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApplicationsQuery } from "@/lib/queries";
import {
  adminResendApprovalEmail,
  adminReviewApplication,
  adminUpdateApplication,
} from "@/lib/admin.functions";
import { reportHandledSync } from "@/lib/observability";

type ApplicationRow = {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  city: string | null;
  state: string | null;
  status: string;
  volunteer_track: string | null;
  availability: string | null;
  t1d_connection: string | null;
  why_help: string;
  programs_of_interest: string[];
  reviewed_at: string | null;
};

type EditForm = {
  full_name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  volunteer_track: string;
  availability: string;
  t1d_connection: string;
  why_help: string;
};

export const Route = createFileRoute("/_authenticated/t1dwpanel/applications")({
  head: () => ({
    meta: [
      { title: "Applications | Warrior Volunteer" },
      { name: "description", content: "Applications in the T1D Warriors volunteer workspace." },
      { property: "og:title", content: "Applications | Warrior Volunteer" },
      { property: "og:description", content: "Applications in the T1D Warriors volunteer workspace." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ApplicationsPage,
});

const TRACK_LABEL: Record<string, string> = {
  remote: "Remote outreach",
  onsite: "On-site crew",
  both: "Remote + on-site",
};

function ApplicationsPage() {
  const { data: applications = [] } = useQuery(adminApplicationsQuery);
  const review = useServerFn(adminReviewApplication);
  const resendApproval = useServerFn(adminResendApprovalEmail);
  const updateApplication = useServerFn(adminUpdateApplication);
  const queryClient = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState("pending");
  const [editing, setEditing] = useState<ApplicationRow | null>(null);
  const [form, setForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    setForm({
      full_name: editing.full_name ?? "",
      email: editing.email ?? "",
      phone: editing.phone ?? "",
      city: editing.city ?? "",
      state: editing.state ?? "",
      volunteer_track: editing.volunteer_track ?? "remote",
      availability: editing.availability ?? "",
      t1d_connection: editing.t1d_connection ?? "",
      why_help: editing.why_help ?? "",
    });
  }, [editing]);

  const visible = (applications as ApplicationRow[]).filter((a) =>
    filter === "all" ? true : a.status === filter,
  );

  async function saveEdit() {
    if (!editing || !form) return;
    setSaving(true);
    try {
      const result = await updateApplication({
        data: {
          id: editing.id,
          full_name: form.full_name,
          email: form.email,
          phone: form.phone,
          city: form.city,
          state: form.state,
          volunteer_track: form.volunteer_track,
          availability: form.availability,
          t1d_connection: form.t1d_connection,
          why_help: form.why_help,
        },
      });
      toast.success(
        result.emailChangedOnApproved
          ? "Application, profile, and sign-in email updated."
          : "Application updated.",
      );
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ["admin"] });
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "updateApplication" });
      toast.error(error instanceof Error ? error.message : "Could not update the application.");
    } finally {
      setSaving(false);
    }
  }

  async function decide(id: string, decision: "approved" | "declined") {
    setBusyId(id);
    try {
      const result = await review({ data: { id, decision } });
      toast.success(
        decision === "approved" && result.provisioned
          ? result.welcomeEmailSent
            ? "Approved. Account provisioned and the welcome email is on its way."
            : "Approved and provisioned, but the welcome email did not send."
          : `Application ${decision}.`,
      );
      queryClient.invalidateQueries({ queryKey: ["admin"] });
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "decide" });
      toast.error(error instanceof Error ? error.message : "Could not update the application.");
    } finally {
      setBusyId(null);
    }
  }

  async function resendWelcome(id: string) {
    setBusyId(id);
    try {
      await resendApproval({ data: { id } });
      toast.success("Welcome email re-sent with a fresh set-password link.");
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "resendWelcome" });
      toast.error(error instanceof Error ? error.message : "Could not send the welcome email.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <AppShell
      eyebrow="Admin"
      title="Applications"
      actions={
        <select
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          className="h-10 rounded-sm border border-input bg-background px-3 text-sm text-ice"
        >
          {["pending", "approved", "declined", "all"].map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      }
    >
      {visible.length === 0 ? (
        <p className="t-body">No {filter === "all" ? "" : filter} applications.</p>
      ) : null}

      <div className="space-y-4">
        {visible.map((application) => (
          <article key={application.id} className="panel p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="t-h3 text-ice">{application.full_name}</h2>
                <p className="t-meta mt-1">
                  {application.email}
                  {application.phone ? ` · ${application.phone}` : ""}
                  {application.city ? ` · ${application.city}, ${application.state ?? ""}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-sm border border-cyan/50 bg-cyan/10 px-3 py-1 text-[0.7rem] font-semibold text-cyan">
                  {TRACK_LABEL[application.volunteer_track ?? "remote"] ?? "Remote outreach"}
                </span>
                <span className="rounded-sm border border-border px-3 py-1 text-[0.7rem] font-semibold text-cyan">
                  {application.status}
                </span>
              </div>
            </div>

            <dl className="mt-5 grid gap-4 md:grid-cols-2">
              <div>
                <dt className="t-meta">Why they want to help</dt>
                <dd className="t-body mt-1 text-sm">{application.why_help}</dd>
              </div>
              <div>
                <dt className="t-meta">T1D connection</dt>
                <dd className="t-body mt-1 text-sm">{application.t1d_connection || "Not shared"}</dd>
              </div>
              <div>
                <dt className="t-meta">Programs of interest</dt>
                <dd className="t-body mt-1 text-sm">
                  {application.programs_of_interest.join(", ") || "None selected"}
                </dd>
              </div>
              <div>
                <dt className="t-meta">Availability</dt>
                <dd className="t-body mt-1 text-sm">{application.availability || "Not shared"}</dd>
              </div>
            </dl>

            {application.status === "pending" ? (
              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  disabled={busyId === application.id}
                  onClick={() => decide(application.id, "approved")}

                >
                  Approve and provision
                </Button>
                <Button
                  variant="outline"
                  disabled={busyId === application.id}
                  onClick={() => decide(application.id, "declined")}

                >
                  Decline
                </Button>
                <Button
                  variant="outline"
                  disabled={busyId === application.id}
                  onClick={() => setEditing(application)}
                >
                  Edit
                </Button>
              </div>
            ) : (
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <p className="t-meta">
                  Reviewed {application.reviewed_at ? new Date(application.reviewed_at).toLocaleString() : ""}
                </p>
                <Button
                  variant="outline"
                  disabled={busyId === application.id}
                  onClick={() => setEditing(application)}
                >
                  Edit
                </Button>
                {application.status === "approved" ? (
                  <Button
                    variant="outline"
                    disabled={busyId === application.id}
                    onClick={() => resendWelcome(application.id)}
                  >
                    Resend welcome email
                  </Button>
                ) : null}
              </div>
            )}
          </article>
        ))}
      </div>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="app-light max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit application</DialogTitle>
            <DialogDescription>
              Fix typos or update contact details before approving. Changes save immediately.
            </DialogDescription>
          </DialogHeader>
          {form ? (
            <div className="grid gap-4 py-2 md:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="ea-name">Full name</Label>
                <Input
                  id="ea-name"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ea-email">Email</Label>
                <Input
                  id="ea-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ea-phone">Phone</Label>
                <Input
                  id="ea-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ea-track">Volunteer track</Label>
                <select
                  id="ea-track"
                  value={form.volunteer_track}
                  onChange={(e) => setForm({ ...form, volunteer_track: e.target.value })}
                  className="h-10 rounded-sm border border-input bg-background px-3 text-sm"
                >
                  <option value="remote">Remote outreach</option>
                  <option value="onsite">On-site crew</option>
                  <option value="both">Remote + on-site</option>
                </select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ea-city">City</Label>
                <Input
                  id="ea-city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ea-state">State</Label>
                <Input
                  id="ea-state"
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ea-availability">Availability</Label>
                <Input
                  id="ea-availability"
                  value={form.availability}
                  onChange={(e) => setForm({ ...form, availability: e.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="ea-t1d">T1D connection</Label>
                <Input
                  id="ea-t1d"
                  value={form.t1d_connection}
                  onChange={(e) => setForm({ ...form, t1d_connection: e.target.value })}
                />
              </div>
              <div className="grid gap-1.5 md:col-span-2">
                <Label htmlFor="ea-why">Why they want to help</Label>
                <textarea
                  id="ea-why"
                  rows={3}
                  value={form.why_help}
                  onChange={(e) => setForm({ ...form, why_help: e.target.value })}
                  className="rounded-sm border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={saving || !form}>
              {saving ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
